import axios from 'axios';
import type {
  ProfileMediaItem,
  ProfileMediaPage,
  ProfileUser,
  TweetFacet,
} from './mediaContracts';
import { toOriginalImageUrl, toThumbnailImageUrl } from './mediaUtils';
import { toSafeHttpUrl } from './safeUrl';

const FX_BASE = 'https://api.fxtwitter.com';

interface FxFormat {
  url?: string;
  bitrate?: number;
  container?: string;
}

interface FxMediaEntity {
  id?: string;
  type?: string;
  url?: string;
  thumbnail_url?: string;
  duration?: number;
  width?: number;
  height?: number;
  formats?: FxFormat[];
  altText?: string;
}

interface FxAuthor {
  id?: string;
  name?: string;
  screen_name?: string;
  avatar_url?: string;
  banner_url?: string;
  description?: string;
  followers?: number;
  following?: number;
  media_count?: number;
}

interface FxFacet {
  type?: string;
  indices?: [number, number];
  original?: string;
  display?: string;
  replacement?: string;
  id?: string;
}

interface FxRawText {
  text?: string;
  display_text_range?: [number, number];
  facets?: FxFacet[];
}

interface FxStatus {
  id?: string;
  url?: string;
  text?: string;
  raw_text?: FxRawText;
  created_at?: string;
  author?: FxAuthor & {
    verification?: { verified?: boolean; type?: string };
  };
  media?: {
    all?: FxMediaEntity[];
    photos?: FxMediaEntity[];
    videos?: FxMediaEntity[];
  };
}

interface FxMediaResponse {
  code?: number;
  results?: FxStatus[];
  cursor?: { top?: string; bottom?: string };
  message?: string;
}

interface FxProfileResponse {
  code?: number;
  message?: string;
  user?: FxAuthor;
}

const mapAuthor = (author?: FxAuthor): ProfileUser | null => {
  if (!author?.screen_name && !author?.id) return null;
  let avatar = author.avatar_url;
  if (avatar) {
    avatar = avatar.replace('_normal.', '_400x400.');
  }
  return {
    id: String(author.id || author.screen_name || ''),
    name: author.name || author.screen_name || '',
    screen_name: author.screen_name || '',
    avatar_url: avatar,
    banner_url: author.banner_url,
    description: author.description,
    followers: author.followers,
    following: author.following,
    media_count: author.media_count,
  };
};

/** Always prefer a canonical x.com status URL so deep-links land on the post. */
const buildTweetUrl = (status: FxStatus): string => {
  const tweetId = String(status.id || '').trim();
  const handle = (status.author?.screen_name || '').replace(/^@/, '').trim();

  if (tweetId && handle) {
    return `https://x.com/${handle}/status/${tweetId}`;
  }
  if (tweetId) {
    return `https://x.com/i/status/${tweetId}`;
  }

  const raw = (status.url || '').trim();
  if (!raw) return '';

  try {
    const parsed = new URL(raw);
    const host = parsed.hostname.replace(/^www\./, '');
    if (
      host === 'x.com' ||
      host === 'twitter.com' ||
      host === 'mobile.twitter.com' ||
      host === 'fxtwitter.com' ||
      host === 'vxtwitter.com'
    ) {
      const path = parsed.pathname;
      const idOnly = path.match(/\/i\/(?:web\/)?status\/(\d+)/i);
      if (idOnly?.[1]) {
        return `https://x.com/i/status/${idOnly[1]}`;
      }
      const withHandle = path.match(/\/([^/]+)\/status\/(\d+)/i);
      if (withHandle?.[1] && withHandle[2]) {
        return `https://x.com/${withHandle[1]}/status/${withHandle[2]}`;
      }
    }
  } catch {
    // fall through
  }

  return toSafeHttpUrl(raw) || '';
};

/** Prefer a real progressive MP4 (never HLS). Highest bitrate for downloads. */
const bestMp4FromFormats = (
  formats: FxFormat[] | undefined,
  fallbackUrl: string
): string => {
  const mp4s = (formats || [])
    .filter(
      (f) =>
        f.url &&
        !f.url.includes('.m3u8') &&
        (f.container === 'mp4' || f.url.includes('.mp4'))
    )
    .sort((a, b) => (b.bitrate || 0) - (a.bitrate || 0));
  let url = mp4s[0]?.url || fallbackUrl;
  if (url.startsWith('http:')) url = url.replace('http:', 'https:');
  return url;
};

const normalizeMediaType = (
  raw?: string
): ProfileMediaItem['type'] => {
  const t = (raw || '').toLowerCase();
  if (t === 'gif' || t === 'animated_gif') return 'gif';
  if (t === 'video') return 'video';
  return 'photo';
};

const mapFacets = (raw?: FxRawText): TweetFacet[] => {
  if (!raw?.facets?.length || !raw.text) return [];

  return raw.facets
    .filter((f) => f.indices && f.indices.length === 2 && f.type)
    .map((f) => {
      const [start, end] = f.indices as [number, number];
      const type = f.type as TweetFacet['type'];
      let href: string | undefined;

      if (type === 'mention' && f.original) {
        href = toSafeHttpUrl(`https://x.com/${f.original}`);
      } else if (type === 'hashtag' && f.original) {
        href = toSafeHttpUrl(`https://x.com/hashtag/${encodeURIComponent(f.original)}`);
      } else if ((type === 'url' || type === 'link') && (f.replacement || f.original)) {
        href = toSafeHttpUrl(f.replacement || f.original);
      } else if (type === 'media' && f.replacement) {
        href = toSafeHttpUrl(f.replacement);
      }

      return {
        type,
        start,
        end,
        original: f.original,
        display: f.display,
        href,
      };
    });
};

const authorExtras = (status: FxStatus) => {
  const author = status.author;
  const text = status.text || status.raw_text?.text || '';
  let avatar = author?.avatar_url;
  if (avatar) {
    avatar = avatar.replace('_normal.', '_400x400.').replace('_200x200.', '_400x400.');
  }
  return {
    author: author?.screen_name,
    authorName: author?.name || author?.screen_name,
    authorAvatar: avatar,
    authorVerified: Boolean(author?.verification?.verified),
    text,
    facets: mapFacets(status.raw_text).filter(
      (f) => f.start < text.length && f.end <= text.length
    ),
  };
};

const mapMediaEntity = (
  entity: FxMediaEntity,
  status: FxStatus
): ProfileMediaItem | null => {
  if (!entity.url && !entity.thumbnail_url) return null;

  const type = normalizeMediaType(entity.type);
  const tweetId = String(status.id || '');
  const tweetUrl = buildTweetUrl(status);

  if (type === 'photo') {
    const sourceUrl = entity.url || '';
    const downloadUrl = toOriginalImageUrl(sourceUrl);
    // Grid previews use a sized thumb so infinite-scroll pages don't starve the viewport with originals.
    const thumbSource = entity.thumbnail_url || sourceUrl;
    const thumbnailUrl =
      toThumbnailImageUrl(thumbSource) ||
      thumbSource ||
      downloadUrl ||
      sourceUrl;
    return {
      tweetId,
      tweetUrl,
      createdAt: status.created_at,
      ...authorExtras(status),
      type: 'photo',
      thumbnailUrl,
      downloadUrl,
      width: entity.width,
      height: entity.height,
    };
  }

  const downloadUrl = bestMp4FromFormats(entity.formats, entity.url || '');
  const variants = (entity.formats || [])
    .filter(
      (f) =>
        f.url &&
        !f.url.includes('.m3u8') &&
        (f.container === 'mp4' || f.url?.includes('.mp4'))
    )
    .map((f) => ({ url: f.url!, bitrate: f.bitrate }));

  const videoThumbSource = entity.thumbnail_url || '';
  const thumbnailUrl = videoThumbSource
    ? toThumbnailImageUrl(videoThumbSource) || videoThumbSource
    : '';

  return {
    tweetId,
    tweetUrl,
    createdAt: status.created_at,
    ...authorExtras(status),
    type,
    thumbnailUrl,
    downloadUrl,
    width: entity.width,
    height: entity.height,
    duration: entity.duration,
    variants: variants.length ? variants : undefined,
  };
};

const collectEntities = (status: FxStatus): FxMediaEntity[] => {
  const media = status.media;
  if (!media) return [];
  if (media.all && media.all.length > 0) return media.all;
  return [...(media.videos || []), ...(media.photos || [])];
};

/**
 * Fetch a user profile from FXTwitter v2.
 */
export async function fetchProfile(handle: string): Promise<ProfileUser> {
  const clean = handle.replace(/^@/, '').trim();
  if (!clean) throw new Error('Please enter a valid @handle');

  try {
    const { data } = await axios.get<FxProfileResponse>(
      `${FX_BASE}/2/profile/${encodeURIComponent(clean)}`,
      {
        headers: {
          Accept: 'application/json',
          'User-Agent': 'XDownloaderPro/1.0',
        },
      }
    );

    if (data.code && data.code !== 200) {
      throw new Error(data.message || 'Profile not found');
    }

    const profile = mapAuthor(data.user);
    if (!profile) {
      throw new Error('Profile not found');
    }
    return profile;
  } catch (err) {
    if (axios.isAxiosError(err)) {
      if (err.response?.status === 404) {
        throw new Error(`User @${clean} was not found`);
      }
      throw new Error(err.message || 'Failed to fetch profile');
    }
    throw err;
  }
}

/**
 * Fetch one page of a user's media timeline.
 * Pass `cursor` from a previous page's `nextCursor` to paginate.
 */
export async function fetchProfileMedia(
  handle: string,
  cursor?: string | null,
  count: number = 20
): Promise<ProfileMediaPage> {
  const clean = handle.replace(/^@/, '').trim();
  if (!clean) throw new Error('Please enter a valid @handle');

  const pageSize = Math.min(100, Math.max(1, count));
  const params: Record<string, string | number> = { count: pageSize };
  if (cursor) params.cursor = cursor;

  try {
    const { data } = await axios.get<FxMediaResponse>(
      `${FX_BASE}/2/profile/${encodeURIComponent(clean)}/media`,
      {
        params,
        headers: {
          Accept: 'application/json',
          'User-Agent': 'XDownloaderPro/1.0',
        },
      }
    );

    if (data.code && data.code !== 200) {
      throw new Error(data.message || 'Failed to load media');
    }

    const results = data.results || [];
    const items: ProfileMediaItem[] = [];
    let profile: ProfileUser | null = null;

    for (const status of results) {
      if (!profile && status.author) {
        profile = mapAuthor(status.author);
      }
      for (const entity of collectEntities(status)) {
        const mapped = mapMediaEntity(entity, status);
        if (mapped) items.push(mapped);
      }
    }

    if (!profile) {
      try {
        profile = await fetchProfile(clean);
      } catch {
        profile = null;
      }
    }

    if (items.length === 0 && !cursor) {
      throw new Error(
        profile
          ? `@${clean} has no public media to show`
          : `No media found for @${clean}`
      );
    }

    // Empty page (or no mapped media) means the timeline is exhausted —
    // do not keep a bottom cursor that would cause infinite scroll retries.
    const rawBottom = data.cursor?.bottom || null;
    const nextCursor =
      items.length === 0 || !rawBottom || rawBottom === cursor ? null : rawBottom;

    return {
      profile,
      items,
      nextCursor,
    };
  } catch (err) {
    if (axios.isAxiosError(err)) {
      if (err.response?.status === 404) {
        throw new Error(`User @${clean} was not found or has no media`);
      }
      throw new Error(err.message || 'Failed to fetch profile media');
    }
    throw err;
  }
}

export type FollowListKind = 'followers' | 'following';

export interface FollowListPage {
  users: ProfileUser[];
  nextCursor: string | null;
}

/**
 * Fetch one page of a user's followers or following list.
 */
export async function fetchFollowList(
  handle: string,
  kind: FollowListKind,
  cursor?: string | null,
  count: number = 40
): Promise<FollowListPage> {
  const clean = handle.replace(/^@/, '').trim();
  if (!clean) throw new Error('Please enter a valid @handle');

  const pageSize = Math.min(100, Math.max(1, count));
  const params: Record<string, string | number> = { count: pageSize };
  if (cursor) params.cursor = cursor;

  try {
    const { data } = await axios.get<{
      code?: number;
      message?: string;
      results?: FxAuthor[];
      cursor?: { top?: string; bottom?: string };
    }>(`${FX_BASE}/2/profile/${encodeURIComponent(clean)}/${kind}`, {
      params,
      headers: {
        Accept: 'application/json',
        'User-Agent': 'XDownloaderPro/1.0',
      },
    });

    if (data.code && data.code !== 200) {
      throw new Error(
        data.message ||
          (kind === 'followers'
            ? 'Failed to load followers'
            : 'Failed to load following')
      );
    }

    const users = (data.results || [])
      .map((u) => mapAuthor(u))
      .filter((u): u is ProfileUser => Boolean(u?.screen_name));

    const rawBottom = data.cursor?.bottom || null;
    const nextCursor =
      users.length === 0 || !rawBottom || rawBottom === cursor ? null : rawBottom;

    return { users, nextCursor };
  } catch (err) {
    if (axios.isAxiosError(err)) {
      if (err.response?.status === 404) {
        throw new Error(
          kind === 'followers'
            ? `Followers for @${clean} are unavailable`
            : `Following for @${clean} are unavailable`
        );
      }
      throw new Error(
        err.message ||
          (kind === 'followers'
            ? 'Failed to load followers'
            : 'Failed to load following')
      );
    }
    throw err;
  }
}
